import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, Typography, Spin, Button, Tooltip } from 'antd';
import { Checkbox as CrceCheckbox } from '@crc-evans/ui-components';
import moment from 'moment';

const { Text } = Typography;

const DSRSectionBreadcrumb = ({
  selectedDate,
  shift,
  sectionTitle,
  skipped,
  onSkipChange,
  isSubmitted = false,
  disabled = false,
  isUpdatingDsrLogSections = false,
  isShiftSkipped = false,
}) => {
  // Local state that syncs with parent but tracks user interaction
  const [localSkipped, setLocalSkipped] = useState(skipped);
  const hasUserInteracted = useRef(false);
  const lastParentValue = useRef(skipped);
  const currentSectionKey = useRef(`${sectionTitle}-${shift}`);

  // Sync with parent component's skip status
  useEffect(() => {
    // Only update if parent value changed and user hasn't interacted
    if (lastParentValue.current !== skipped) {
      lastParentValue.current = skipped;
      // If user hasn't interacted, sync with parent
      if (!hasUserInteracted.current) {
        setLocalSkipped(skipped);
      }
    }
  }, [skipped, sectionTitle]);

  // Reset user interaction only when section or shift actually changes
  useEffect(() => {
    const newSectionKey = `${sectionTitle}-${shift}`;
    if (currentSectionKey.current !== newSectionKey) {
      currentSectionKey.current = newSectionKey;
      hasUserInteracted.current = false;
      setLocalSkipped(skipped);
      lastParentValue.current = skipped;
    }
  }, [sectionTitle, shift, skipped]);

  const handleSkipChange = (checkedOrEvent) => {
    const newValue = typeof checkedOrEvent === 'boolean'
      ? checkedOrEvent
      : (checkedOrEvent?.target?.checked ?? false);
    setLocalSkipped(newValue);
    hasUserInteracted.current = true;
    onSkipChange({ target: { checked: newValue } });
  };

  // Format the selected date or use today's date as fallback
  const displayDate = selectedDate
    ? moment(selectedDate).format('DD-MMM-YYYY')
    : moment().format('DD-MMM-YYYY');

  return (
    <div
      className="modern-dsr-section-bc-row"
      style={{
        padding: '2px 0 2px 0',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 16,
        minHeight: 32,
        minWidth: 0,
        flex: '0 1 auto',
      }}
    >
      <Text
        type="secondary"
        className="modern-dsr-section-bc-date"
        style={{
          fontSize: '14px',
          color: '#334155',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          lineHeight: 1.35,
        }}
      >
        Date: {displayDate}
      </Text>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isUpdatingDsrLogSections && (
          <Spin
            size="small"
            style={{ marginRight: 4 }}
          />
        )}

        {isShiftSkipped ? (
          <div
            style={{
              color: '#faad14',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: 'rgba(250, 173, 20, 0.1)',
              padding: '4px 10px',
              borderRadius: '25px',
              border: '1px solid #faad14',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
          </div>
        ) : (
          <CrceCheckbox
            label={`Skip ${sectionTitle}`}
            checked={localSkipped}
            onChange={handleSkipChange}
            disabled={disabled || isUpdatingDsrLogSections}
            borderColor="#00a88e"
          />
        )}
      </div>
    </div>
  );
};

export default DSRSectionBreadcrumb;
